import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import WelcomeUser from '../welcome-user/welcome-user';

import { Button, Grid, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Storage } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getUserPreferencesData, sendUserPreferencesData } from 'app/shared/reducers/user-preferences';

const MAX_CHARACTERS = 250;

const SetBio = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<string>('');
  const [charCount, setCharCount] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [userPreferences, setUserPreferences] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const matchesXs = useMediaQuery('(max-width:500px)');
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const breakpoint = useMediaQuery(theme.breakpoints.down('sm'));

  const darkModeEnabled = useAppSelector(state => state.darkMode).darkMode;
  const sendDataSuccess = useAppSelector(state => state.userPreferencesSettings.sendSuccess);

  // Load saved values from session storage
  useEffect(() => {
    if (Storage.session.get('bio')) {
      setData(Storage.session.get('bio'));
    }
    if (Storage.session.get('weekDays')) {
      const weekDaysData = JSON.parse(Storage.session.get('weekDays'));
      const weekDays = weekDaysData.map(day => day.value);
      setUserPreferences(prev => ({ ...prev, weekDays }));
    }
    if (Storage.session.get('location')) {
      const locationMasterDTO = Storage.session.get('location');
      setUserPreferences(prev => ({ ...prev, locationMasterDTO }));
    }
    if (Storage.session.get('preferredStartTime')) {
      const preferredStartTime = Storage.session.get('preferredStartTime');
      setUserPreferences(prev => ({ ...prev, preferredStartTime }));
    }
    if (Storage.session.get('preferredEndTime')) {
      const preferredEndTime = Storage.session.get('preferredEndTime');
      setUserPreferences(prev => ({ ...prev, preferredEndTime }));
    }
    if (Storage.session.get('interests')) {
      const interestsData = JSON.parse(Storage.session.get('interests'));
      const interests = interestsData.map(interest => interest.id);
      setUserPreferences(prev => ({ ...prev, interests }));
    }
    if (Storage.session.get('Maxdistance')) {
      const distanceLimit = JSON.parse(Storage.session.get('Maxdistance'));
      setUserPreferences(prev => ({ ...prev, distanceLimit }));
    }
    if (Storage.session.get('profilePic')) {
      const profilePic = Storage.session.get('profilePic');
      setUserPreferences(prev => ({ ...prev, profilePic: profilePic.id }));
    }
    if (Storage.session.get('DistanceUnit')) {
      const distanceUnit = Storage.session.get('DistanceUnit');
      setUserPreferences(prev => ({ ...prev, distanceUnit }));
    }

    setUserPreferences(prev => ({ ...prev, darkModeEnabled }));
  }, []);

  // Set char count for stored bio
  useEffect(() => {
    const storedBio = Storage.session.get('bio');
    if (storedBio) {
      setData(storedBio);
      setCharCount(storedBio.length);
    }
  }, []);

  // After saving success
  useEffect(() => {
    if (sendDataSuccess) {
      Storage.session.remove('preferredStartTime');
      Storage.session.remove('weekDays');
      Storage.session.remove('location');
      Storage.session.remove('preferredEndTime');
      Storage.session.remove('interests');
      Storage.session.remove('distance');
      Storage.session.remove('bio');
      Storage.session.remove('ActiveField');
      Storage.session.remove('defaultDistance');
      Storage.session.remove('Maxdistance');
      Storage.session.remove('id');
      Storage.session.remove('profilePic');
      Storage.session.remove('DistanceUnit');

      dispatch(getUserPreferencesData());
      navigate('/');
    }
  }, [sendDataSuccess]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARACTERS) {
      setData(text);
      setCharCount(text.length);
      setError('');
      setIsSubmitting(false);
    } else {
      setIsSubmitting(true);
      setError(`Bio cannot exceed ${MAX_CHARACTERS} characters.`);
    }
  };

  const handleUserPreferencesSubmit = () => {
    if (charCount > MAX_CHARACTERS) {
      setError(`Bio cannot exceed ${MAX_CHARACTERS} characters.`);
      return;
    }

    const myDetails = JSON.parse(localStorage.getItem('MyDetailsAccount'));
    const userId = myDetails?.id || 0;

    setUserPreferences(prev => ({ ...prev, description: data }));
    setIsSubmitting(true);

    const finalPayload = {
      distanceLimit:
        userPreferences?.distanceUnit === 'km'
          ? userPreferences?.distanceLimit
          : userPreferences?.distanceLimit * 1.61,
      distanceUnit: userPreferences?.distanceUnit,
      profilePic: userPreferences?.profilePic,
      description: data,
      preferredStartTime: userPreferences?.preferredStartTime,
      preferredEndTime: userPreferences?.preferredEndTime,
      interests: userPreferences?.interests,
      weekDays: userPreferences?.weekDays,
      locationMasterDTO: {
        city: userPreferences?.locationMasterDTO?.city,
        state: userPreferences?.locationMasterDTO?.state,
        country: userPreferences?.locationMasterDTO?.country,
        latitude: String(userPreferences?.locationMasterDTO?.latitude),
        longitude: String(userPreferences?.locationMasterDTO?.longitude),
      },
      darkModeEnabled: darkModeEnabled,
    };

    dispatch(sendUserPreferencesData(finalPayload));
  };

  return (
    <Grid container direction="column" minHeight="100vh" py={5} sx={{ padding: matchesXs ? '5%' : '' }}>
      <Grid item>
        <WelcomeUser />
      </Grid>

      <Grid item container direction="column" sx={{ alignItems: 'center' }} justifyContent={'center'} gap={2}>
        <Grid item>
          <Typography variant="h4" sx={{ color: 'secondary.light', fontSize: matchesXs ? '20px' : '24px' }}>
            Let people know you better
          </Typography>
        </Grid>

        <Grid item>
          <Stack direction={'column'} alignItems={'center'}>
            <Grid item>
              <TextField
                name="bio"
                id="bio"
                defaultValue={data}
                rows={4}
                placeholder="Enter your short bio"
                multiline
                sx={{
                  width: breakpoint ? '335px' : '600px',
                  '& .MuiInputBase-input': {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'normal',
                    overflowWrap: 'break-word',
                  },
                }}
                onChange={handleChange}
                error={!!error}
                helperText={error || `${charCount}/${MAX_CHARACTERS} characters`}
              />
            </Grid>

            <Grid item container alignItems="center" justifyContent={'space-between'} my={3}>
              <Button
                onClick={() => {
                  Storage.session.set('bio', data);
                  navigate('/interests');
                }}
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  width: '89px',
                  height: '40px',
                  borderRadius: '15px',
                  border: '1px solid #FFCE00',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                <Typography>Back</Typography>
              </Button>

              <Button
                onClick={handleUserPreferencesSubmit}
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  width: '89px',
                  height: '40px',
                  borderRadius: '15px',
                  border: '1px solid #FFCE00',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: 'primary.main', boxShadow: 'none' },
                }}
              >
                <Typography>Finish</Typography>
              </Button>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SetBio;
